import { FC, memo } from 'react'
import { Box, Toolbar } from '@perk-ui/core'
import { Route, Switch, useLocation } from 'react-router'

import { pathsWithoutHeader } from '../components/AppHeader'
import PrivateRoute from '../components/PrivateRoute'
import config from '../config/app-config'
import AnimatedRedirect from '../features/animation/AnimatedRedirect'
import { useAuth } from '../features/auth/AuthContext'
import ActivateAccount from './ActivateAccount/ActivateAccount'
import Adjudication from './AdverseEvents/Adjudication/Adjudication'
import AdverseEvents from './AdverseEvents/AdverseEvents'
import AccountVerification from './Authentication/AccountVerification/AccountVerification'
import NewActivateAccount from './Authentication/ActivateAccount/ActivateAccount'
import NewForgotPassword from './Authentication/ForgotPassword/ForgotPassword'
import NewLogin from './Authentication/Login/Login'
import NewPasswordReset from './Authentication/PasswordReset/PasswordReset'
import ProviderRegister from './Authentication/Register/ProviderRegister'
import NewRegister from './Authentication/Register/Register'
import NewResendActivationLink from './Authentication/ResendActivationLink/ResendActivationLink'
import Data from './Data/Data'
import ForgotPassword from './ForgotPassword/ForgotPassword'
import Home from './Home/Home'
import MyImpact from './Insights/MyImpact'
import MyStudies from './Insights/MyStudies'
import MyTeams from './Insights/MyTeams'
import RawData from './Insights/RawData'
import Login from './Login/Login'
import PasswordReset from './PasswordReset/PasswordReset'
import PatientCaseSurveys from './PatientCaseSurveys/PatientCaseSurveys'
import PatientDetails from './PatientDetails/PatientDetails'
import PatientFlow from './PatientFlow/PatientFlow'
import PatientFlowUrls from './PatientFlow/PatientFlowUrls'
import Patients from './Patients/Patients'
import PatientsCD from './PatientsCD/PatientsCD'
import PatientSelfOnboarding from './PatientSelfOnboarding/PatientSelfOnboarding'
import PatientSelfOnboardUrls from './PatientSelfOnboarding/PatientSelfOnboardUrls'
import ProtocolFlow from './ProtocolFlow/ProtocolFlow'
import ProtocolFlowUrls from './ProtocolFlow/ProtocolFlowUrls'
import Provider from './Provider/Provider'
import QuickStats from './QuickStats/QuickStats'
import ResendActivationLink from './ResendActivationLink/ResendActivationLink'

const Routes: FC = memo(() => {
  const { isAuthenticated } = useAuth()
  const { pathname, search } = useLocation()
  const isCurrentLocation = (path: string) => pathname.startsWith(path)
  const showHeader = !pathsWithoutHeader.some(isCurrentLocation)

  return (
    <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflow: 'auto' }}>
      {showHeader && <Toolbar style={{ height: '64px' }} />}
      <Switch>
        {/**
         * Authentication (OLD)
         */}
        <Route path={config.unAuthenticatedRootUrl} component={Login} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/password-reset/:token" component={PasswordReset} />
        <Route path="/activate-account/:token" component={ActivateAccount} />
        <Route
          path="/resend-activation-link"
          component={ResendActivationLink}
        />

        {/**
         * Provider Registration (NEW)
         */}
        <Route path="/register-provider/:token" component={ProviderRegister} />
        <Route path="/register-provider" component={ProviderRegister} />

        {/**
         * Provider Flow (Protected)
         */}
        <PrivateRoute path="/provider" component={Provider} />

        {/**
         * Authentication (UPDATED)
         */}
        {!__PROD__ && (
          <Route path="/create-account/:token" component={NewRegister} />
        )}
        {!__PROD__ && <Route path="/create-account" component={NewRegister} />}
        {!__PROD__ && (
          <Route
            path="/account-verification/:token"
            component={AccountVerification}
          />
        )}
        {!__PROD__ && <Route path="/login-new" component={NewLogin} />}
        {!__PROD__ && (
          <Route path="/forgot-password-new" component={NewForgotPassword} />
        )}
        {!__PROD__ && (
          <Route
            path="/password-reset-new/:token"
            component={NewPasswordReset}
          />
        )}
        {!__PROD__ && (
          <Route
            path="/activate-account-new/:token"
            component={NewActivateAccount}
          />
        )}
        {!__PROD__ && (
          <Route
            path="/resend-activation-link-new"
            component={NewResendActivationLink}
          />
        )}

        {/**
         * Home
         */}
        <PrivateRoute path="/home" component={Home} />

        {/**
         * Patient Flow
         */}
        <PrivateRoute
          path={PatientFlowUrls.base.match}
          component={PatientFlow}
        />

        {/**
         * Patients
         */}
        <PrivateRoute
          path="/patients/:patientId/patient-case-surveys/:caseId"
          component={PatientCaseSurveys}
        />
        <PrivateRoute path="/patients/:patientId" component={PatientDetails} />
        <PrivateRoute path="/patientsCD" component={Patients} />
        <PrivateRoute path="/patients" component={PatientsCD} />
        {/**
         * Adverse Events
         */}
        <PrivateRoute
          path="/adverse-events/:adverseEventId"
          component={Adjudication}
        />
        <PrivateRoute path="/adverse-events" component={AdverseEvents} />

        {/**
         * Data
         */}
        <PrivateRoute path="/data" component={Data} />

        {/* Insights */}
        <PrivateRoute path="/my-impact" component={MyImpact} />
        <PrivateRoute path="/my-teams" component={MyTeams} />
        <PrivateRoute path="/my-studies" component={MyStudies} />
        <PrivateRoute path="/raw-data" component={RawData} />

        {/* Quick Stats */}
        <PrivateRoute path="/quick-stats" component={QuickStats} />

        {/**
         * Protocol Flow
         */}
        <PrivateRoute
          path={ProtocolFlowUrls.base.match}
          component={ProtocolFlow}
        />

        {/**
         * Patient self onboarding
         */}
        <PrivateRoute
          path={PatientSelfOnboardUrls.base.match}
          component={PatientSelfOnboarding}
        />

        {/**
         * 404
         */}

        {/**
         * Catch-all
         */}
        <Route
          path="*"
          render={() => (
            <AnimatedRedirect
              to={{
                pathname: isAuthenticated
                  ? config.authenticatedRootUrl
                  : config.unAuthenticatedRootUrl,
                search: search,
              }}
            />
          )}
        />
      </Switch>
    </Box>
  )
})

export default Routes
